// src/pages/OrderDetails.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  getOrderDetails,
  getOrderCompanies,
  updateOrderStatus,
  getOrderComments,
  addOrderComment,
  editOrderComment,
  deleteOrderComment,
  checkAccess,
  ALLOWED_ROLES
} from '../api';
import { useTranslation } from 'react-i18next';
import StatusBadge from '../components/StatusBadge';
import Icon from '../components/Icon';
import CustomSelect from '../components/CustomSelect';
import ImageLightbox from '../components/ImageLightbox';
import '../css/user-details.css';
import '../css/order-details.css';
import { pushDetailNavState } from '../utils/detailNavState';

const InfoRow = ({ label, value, highlight }) => (
  <div className="info-row">
    <span className="info-label">{label}</span>
    <span className={`info-value ${highlight ? 'highlight' : ''}`} style={{ whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'right' }}>
      {value || '—'}
    </span>
  </div>
);

/** Сколько фото заявки показывать до нажатия «Ещё» */
const PHOTOS_PREVIEW_LIMIT = 5;

const ORDER_STATUS_SEQUENCE = ['IN_PROGRESS', 'IN_WORK', 'DONE', 'DONE_DELETED', 'CANCELED', 'BLOCKED'];
/** Для этих статусов нужно явно указать компанию (мастера), закреплённую за заказом */
const STATUSES_REQUIRING_MASTER = ['IN_WORK', 'DONE'];
/** Иконки как в фильтре заказов (CustomSelect на странице поиска заказов) */
const ORDER_STATUS_ICONS = {
  IN_PROGRESS: 'clock',
  IN_WORK: 'briefcase',
  DONE: 'check-circle',
  DONE_DELETED: 'trash-2',
  CANCELED: 'x-circle',
  BLOCKED: 'slash'
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [canEditStatus, setCanEditStatus] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedMasterId, setSelectedMasterId] = useState('');
  const [message, setMessage] = useState(null);
  const [photosExpanded, setPhotosExpanded] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const [activeOrderTab, setActiveOrderTab] = useState('main');
  const [orderComments, setOrderComments] = useState([]);
  const [orderCommentsLoading, setOrderCommentsLoading] = useState(false);
  const [interactionNote, setInteractionNote] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [currentRole, setCurrentRole] = useState(null);

  const locale = i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'ro' ? 'ro-RO' : 'en-US';
  const statusLabels = t('orders.statusLabels', { returnObjects: true });
  const sectionTexts = t('orderDetails.sections', { returnObjects: true }) || {};

  const orderStatusesInline = useMemo(
    () =>
      ORDER_STATUS_SEQUENCE.map((id) => ({
        id,
        label: statusLabels[id] || id,
        icon: ORDER_STATUS_ICONS[id] || 'package'
      })),
    [statusLabels]
  );

  const masterOptions = useMemo(
    () => [
      { value: '', label: t('orderDetails.interaction.masterPlaceholder'), icon: 'layers' },
      ...companies.map((company) => ({
        value: company.id,
        label: company.fullName || company.id,
        icon: 'briefcase'
      }))
    ],
    [companies, t]
  );

  useEffect(() => {
    const loadRole = async () => {
      try {
        const role = await checkAccess();
        setCurrentRole(role);
      } catch (err) {
        console.error('OrderDetails: checkAccess failed', err);
      }
    };
    loadRole();
  }, []);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getOrderDetails(orderId);
        setOrder(data);
        setSelectedMasterId(data?.companyId || '');
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError(err.message || 'Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderDetails();
    } else {
      setError('Order ID is missing');
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!orderId) return;
      try {
        setCompaniesLoading(true);
        const data = await getOrderCompanies(orderId);
        const companiesList = Array.isArray(data) ? data : (data.content || data.companies || []);
        setCompanies(companiesList);
      } catch (err) {
        console.error('Error fetching companies:', err);
        setCompanies([]);
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchCompanies();
  }, [orderId]);

  useEffect(() => {
    const loadComments = async () => {
      if (!orderId) return;
      try {
        setOrderCommentsLoading(true);
        const comments = await getOrderComments(orderId);
        setOrderComments(comments || []);
      } catch (err) {
        console.error('Error loading order comments:', err);
      } finally {
        setOrderCommentsLoading(false);
      }
    };
    loadComments();
  }, [orderId]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setCanEditStatus(false);
      return;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const role = payload.role || payload.userRole || payload.authority;
      const normalized = role?.toString().trim().toUpperCase();
      if (normalized === ALLOWED_ROLES.MODERATOR || normalized === ALLOWED_ROLES.ADMIN || normalized === ALLOWED_ROLES.DEV) {
        setCanEditStatus(true);
      } else {
        setCanEditStatus(false);
      }
    } catch (e) {
      console.error('Failed to parse token role', e);
      setCanEditStatus(false);
    }
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const displayValue = (value, fallback = 'N/A') => value || fallback;

  const imageFiles = Array.isArray(order?.filesUrl)
    ? order.filesUrl.filter((file) => file?.contentType?.toLowerCase().startsWith('image'))
    : [];

  const formatFileName = (name) => {
    if (!name) return 'image';
    try {
      return decodeURIComponent(name).replace(/\+/g, ' ');
    } catch {
      return name;
    }
  };

  const lightboxImages = imageFiles
    .map((file) => ({ url: file.fileUrl, name: formatFileName(file.fileName) }))
    .filter((image) => image.url);
  const visibleImageFiles = photosExpanded ? imageFiles : imageFiles.slice(0, PHOTOS_PREVIEW_LIMIT);
  const hiddenPhotosCount = imageFiles.length - visibleImageFiles.length;

  const formatAddress = (address) => {
    if (!address) return t('users.labels.addressNotProvided');
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.house) parts.push(address.house);
    if (address.apartment) parts.push(address.apartment);
    if (address.location?.name) parts.push(address.location.name);
    if (address.region?.name) parts.push(address.region.name);
    return parts.length > 0 ? parts.join(', ') : t('users.labels.addressNotProvided');
  };

  const formatCategory = (category) => {
    if (!category) return 'N/A';
    return category.title || category.name || 'N/A';
  };

  const handleOrderStatusChange = async (newStatus) => {
    if (!order || newStatus === order.status) return;
    if (STATUSES_REQUIRING_MASTER.includes(newStatus) && !selectedMasterId) {
      setMessage({ type: 'error', text: t('orderDetails.interaction.masterRequired') });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    const newLabel = statusLabels[newStatus] || newStatus;
    if (!window.confirm(t('orderDetails.confirmStatusChange', { status: newLabel }))) {
      return;
    }
    setUpdatingStatus(true);
    try {
      const updated = await updateOrderStatus(
        order.id,
        newStatus,
        STATUSES_REQUIRING_MASTER.includes(newStatus) ? selectedMasterId : undefined
      );
      setOrder(updated);
      setSelectedMasterId(updated?.companyId || selectedMasterId);
      setMessage({ type: 'success', text: t('common.saved') });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Failed to update order status', err);
      setMessage({ type: 'error', text: err.message || 'Error updating status' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStoredAuthUser = () => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const enrichCommentFromSession = (raw) => {
    if (!raw) return raw;
    const auth = getStoredAuthUser();
    const name =
      raw.authorFullName ||
      raw.authorName ||
      raw.createdByName ||
      auth?.fullName ||
      auth?.username ||
      '';
    const role = raw.authorRole || auth?.role || currentRole || undefined;
    return { ...raw, authorFullName: name || raw.authorFullName, authorRole: role };
  };

  const handleSaveOrderNote = async () => {
    if (!interactionNote.trim() || !orderId) return;
    try {
      const comment = await addOrderComment(orderId, interactionNote.trim());
      const enriched = enrichCommentFromSession(comment);
      setOrderComments((prev) => [enriched, ...(prev || [])]);
      setMessage({ type: 'success', text: t('userDetails.messages.noteSaved') });
      setInteractionNote('');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Order note save failed:', err);
      setMessage({ type: 'error', text: err.message || t('common.error') });
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const startEditComment = (comment) => {
    const id = comment.id || comment.commentId;
    if (!id) return;
    setEditingCommentId(id);
    setEditingCommentText(comment.text || comment.comment || '');
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleUpdateOrderComment = async () => {
    if (!editingCommentId || !editingCommentText.trim()) return;
    try {
      const updated = await editOrderComment(editingCommentId, editingCommentText.trim());
      const merged = enrichCommentFromSession({ ...updated });
      setOrderComments((prev) =>
        (prev || []).map((c) => {
          const cid = c.id || c.commentId;
          return cid === editingCommentId ? merged : c;
        })
      );
      setMessage({ type: 'success', text: t('userDetails.messages.noteSaved') });
      setEditingCommentId(null);
      setEditingCommentText('');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Order note update failed:', err);
      setMessage({ type: 'error', text: err.message || t('common.error') });
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleDeleteOrderComment = async (comment) => {
    const id = comment.id || comment.commentId;
    if (!id) return;
    if (!window.confirm(t('orderDetails.interaction.deleteConfirm'))) {
      return;
    }
    try {
      await deleteOrderComment(id);
      setOrderComments((prev) => (prev || []).filter((c) => (c.id || c.commentId) !== id));
      setMessage({ type: 'success', text: t('common.saved') });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Order note delete failed:', err);
      setMessage({ type: 'error', text: err.message || t('common.error') });
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleCompanyClick = (companyId) => {
    handleOpenUserDetails(companyId);
  };

  const handleOpenUserDetails = (userId) => {
    if (!userId) return;
    navigate(`/users/${userId}`, {
      state: pushDetailNavState(location, { fromOrder: location.pathname }),
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage({ type: 'success', text: t('userDetails.messages.copied') });
      setTimeout(() => setMessage(null), 2000);
    });
  };

  if (loading) {
    return (
      <div className="user-details-page">
        <div className="loading-state">
          <div className="strict-loader"></div>
          <div>{t('orderDetails.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-details-page">
        <div className="error-state">
          <div className="error-icon" style={{ marginBottom: '16px', color: '#ef4444' }}><Icon name="alert-triangle" size={48} /></div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 8px 0', color: 'var(--text-primary)' }}>{t('orderDetails.error')}</h2>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="user-details-page">
        <div className="error-state">
          <div className="error-icon" style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}><Icon name="search" size={48} /></div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 8px 0', color: 'var(--text-primary)' }}>{t('orderDetails.notFound')}</h2>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{t('orderDetails.missing', { id: orderId })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-details-page">
      {message && (
        <div className={`message-toast ${message.type}`}>
          {message.text}
          <button className="message-close" type="button" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="user-profile-dashboard">
        <aside className="profile-sidebar glass-panel">
          <div className="sidebar-header">
            <div className="sidebar-avatar-placeholder" style={{ background: 'linear-gradient(135deg, #8b5cf6, #a855f7)' }}>
              <Icon name="orders" size={36} />
            </div>
            <h2 className="sidebar-name">{order.title || t('users.labels.notProvided')}</h2>
            <div className="status-actions order-sidebar-status-wrap">
              <StatusBadge type="order" status={order.status} className="order-sidebar-status-badge" />
            </div>
          </div>

          <div className="sidebar-nav order-sidebar-tabs" role="navigation">
            {[
              { id: 'main', icon: 'info', label: t('orderDetails.tabs.main') },
              { id: 'interaction', icon: 'message-circle', label: t('orderDetails.tabs.interaction') }
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`nav-tab-side ${activeOrderTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveOrderTab(tab.id)}
              >
                {activeOrderTab === tab.id && <div className="nav-side-active-bg" />}
                <span className="nav-tab-side-content">
                  <Icon name={tab.icon} size={16} /> {tab.label}
                </span>
              </button>
            ))}
          </div>

          {order.tags && order.tags.length > 0 && (
            <div className="sidebar-quick-stats order-sidebar-tags-block">
              <div className="categories-compact">
                <span className="category-title-small" style={{ color: 'var(--text-secondary)' }}>{t('orderDetails.fields.tags')}</span>
                <div className="category-tags-compact">
                  {order.tags.map((tag) => (
                    <span key={tag} className="category-tag-small">
                      {tag.replaceAll('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </aside>

        <main className="profile-dashboard-main">
          {activeOrderTab === 'main' && (
          <div className="bento-grid-layout">
            <div className="bento-card bento-small">
              <div className="card-header">
                <h3 className="card-title">
                  <span className="card-icon-circle"><Icon name="package" size={13} /></span>
                  {t('orderDetails.summary.title')}
                </h3>
              </div>
              <div className="card-content">
                <div className="info-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '6px', borderBottom: '1px solid rgba(0,0,0,0.06)', paddingBottom: '12px', marginBottom: '4px' }}>
                  <span className="info-label">ID</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <span className="info-value mono" style={{ fontSize: '0.85rem', color: '#10b981', wordBreak: 'break-all', lineHeight: 1.35, textAlign: 'left', flex: 1 }}>
                      {order.id}
                    </span>
                    <button
                      type="button"
                      className="btn-copy hover-trigger"
                      onClick={() => copyToClipboard(order.id)}
                      title={t('userDetails.messages.copied')}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-secondary)', flexShrink: 0 }}
                    >
                      <Icon name="copy" size={14} />
                    </button>
                  </div>
                </div>
                <InfoRow label={t('orderDetails.fields.category')} value={formatCategory(order.category)} />
                <InfoRow label={t('orderDetails.timeline.createdAt')} value={formatDate(order.createdAt)} />
                <InfoRow
                  label={t('orderDetails.fields.price')}
                  value={order.price != null ? `${order.price} MDL` : '—'}
                  highlight
                />
              </div>
            </div>

            <div className="bento-card bento-small">
              <div className="card-header">
                <h3 className="card-title">
                  <span className="card-icon-circle"><Icon name="info" size={13} /></span>
                  {sectionTexts.main}
                </h3>
              </div>
              <div className="card-content">
                <div className="info-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px', borderBottom: 'none' }}>
                  <span className="info-label">{t('orderDetails.fields.description')}</span>
                  <span className="info-value" style={{ textAlign: 'left', width: '100%', whiteSpace: 'pre-wrap', lineHeight: '1.5', wordBreak: 'break-word' }}>
                    {displayValue(order.description)}
                  </span>
                </div>
              </div>
            </div>

            {order.userProfileForOrderDto && (
              <div
                className="bento-card bento-small"
                onClick={() => handleOpenUserDetails(order.userProfileForOrderDto.userProfileId)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-header">
                  <h3 className="card-title">
                    <span className="card-icon-circle"><Icon name="user" size={13} /></span>
                    {sectionTexts.customer}
                  </h3>
                  <Icon name="chevron-right" size={16} style={{ opacity: 0.5 }} />
                </div>
                <div className="card-content">
                  <div className="profile-header" style={{ gap: '16px', padding: '8px 0', display: 'flex', alignItems: 'center' }}>
                    {order.userProfileForOrderDto.avatar ? (
                      <img src={order.userProfileForOrderDto.avatar} alt="Avatar" className="sidebar-avatar" style={{ width: '64px', height: '64px' }} />
                    ) : (
                      <div className="sidebar-avatar-placeholder" style={{ width: '64px', height: '64px', fontSize: '1.5rem', flexShrink: 0 }}>
                        {order.userProfileForOrderDto.fullName?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="profile-info" style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
                      <div className="profile-name" style={{ fontSize: '1.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {order.userProfileForOrderDto.fullName || t('users.labels.notProvided')}
                      </div>
                      <div className="profile-meta" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Icon name="phone" size={14} />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{order.userProfileForOrderDto.defaultPhoneNumber || t('users.labels.notProvided')}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Icon name="mail" size={14} />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{order.userProfileForOrderDto.email || t('users.labels.notProvided')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {order.companyId && (
              <div
                className="bento-card bento-small"
                onClick={() => handleCompanyClick(order.companyId)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-header">
                  <h3 className="card-title">
                    <span className="card-icon-circle"><Icon name="briefcase" size={13} /></span>
                    {t('orderDetails.sections.executingCompany')}
                  </h3>
                  <Icon name="chevron-right" size={16} style={{ opacity: 0.5 }} />
                </div>
                <div className="card-content">
                  <div className="profile-header" style={{ gap: '16px', padding: '8px 0', display: 'flex', alignItems: 'center' }}>
                    <div className="sidebar-avatar-placeholder" style={{ width: '64px', height: '64px', fontSize: '1.5rem', flexShrink: 0 }}>
                      <Icon name="briefcase" size={24} />
                    </div>
                    <div className="profile-info" style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
                      <div className="profile-name" style={{ fontSize: '1.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t('orderDetails.sections.executingCompany')}
                      </div>
                      <div className="profile-meta" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px', width: '100%', minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '100%' }}>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600, flexShrink: 0 }}>ID:</span>
                          <span className="mono" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.8rem', color: '#10b981', minWidth: 0 }}>{order.companyId}</span>
                          <button type="button" className="btn-copy hover-trigger" onClick={(e) => { e.stopPropagation(); copyToClipboard(order.companyId); }} title={t('userDetails.messages.copied')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-secondary)', display: 'flex', flexShrink: 0 }}>
                            <Icon name="copy" size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bento-card bento-small">
              <div className="card-header">
                <h3 className="card-title">
                  <span className="card-icon-circle"><Icon name="map-pin" size={13} /></span>
                  {t('orderDetails.address.title')}
                </h3>
              </div>
              <div className="card-content">
                <div className="info-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px', borderBottom: 'none' }}>
                  <span className="info-value" style={{ textAlign: 'left', fontWeight: 600, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {formatAddress(order.address)}
                  </span>
                  {order.address?.location?.name && (
                    <span className="info-label" style={{ textAlign: 'left', marginTop: '4px', whiteSpace: 'normal' }}>
                      {order.address.location.name}, {order.address.region?.name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="bento-card bento-small">
              <div className="card-header">
                <h3 className="card-title">
                  <span className="card-icon-circle"><Icon name="calendar" size={13} /></span>
                  {t('orderDetails.timeline.title')}
                </h3>
              </div>
              <div className="card-content">
                <InfoRow label={t('orderDetails.timeline.startDate')} value={formatDate(order.startDate)} />
                <InfoRow label={t('orderDetails.timeline.createdAt')} value={formatDate(order.createdAt)} />
                <InfoRow label={t('orderDetails.timeline.updatedAt')} value={formatDate(order.updatedAt)} />
              </div>
            </div>

            <div className="bento-card bento-small">
              <div className="card-header" style={{ marginBottom: '16px' }}>
                <h3 className="card-title">
                  <span className="card-icon-circle"><Icon name="briefcase" size={13} /></span>
                  {sectionTexts.companiesLabel}
                </h3>
              </div>
              <div className="card-content">
                {companiesLoading ? (
                  <div className="loading-state" style={{ padding: '2rem' }}><div className="strict-loader"></div></div>
                ) : companies.length > 0 ? (
                  <div className="services-compact" style={{ padding: 0 }}>
                    {companies.map((company) => (
                      <div
                        key={company.id}
                        className="service-compact"
                        onClick={() => handleCompanyClick(company.id)}
                        style={{ cursor: 'pointer', padding: '16px' }}
                      >
                        <div className="service-name-small">{company.fullName || 'N/A'}</div>
                        <div className="service-price-small" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#d97706', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Icon name="star-fill" size={12} />
                          <span>{company.companyRating?.toFixed(1) || '0.0'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state" style={{ padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <Icon name="briefcase" size={32} style={{ opacity: 0.3 }} />
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{t('orderDetails.companies.empty')}</p>
                  </div>
                )}
              </div>
            </div>

            {imageFiles.length > 0 && (
              <div className="bento-card bento-large">
                <div className="card-header" style={{ marginBottom: '16px' }}>
                  <h3 className="card-title">
                    <span className="card-icon-circle"><Icon name="image" size={13} /></span>
                    {t('orderDetails.photos.title')}
                  </h3>
                </div>
                <div className="card-content">
                  <div className="photos-grid-styled">
                    {visibleImageFiles.map((file) => (
                      <div key={file.fileKey || file.fileUrl} className="photo-item-styled">
                        <button
                          type="button"
                          className="photo-link"
                          onClick={() => setLightboxIndex(
                            lightboxImages.findIndex((image) => image.url === file.fileUrl)
                          )}
                          title={formatFileName(file.fileName)}
                        >
                          <img src={file.fileUrl} alt={formatFileName(file.fileName)} className="photo-image" />
                          <div className="photo-overlay"><Icon name="eye" size={24} className="photo-icon" /></div>
                        </button>
                      </div>
                    ))}
                  </div>
                  {imageFiles.length > PHOTOS_PREVIEW_LIMIT && (
                    <button
                      type="button"
                      className="photos-toggle-btn"
                      onClick={() => setPhotosExpanded((prev) => !prev)}
                    >
                      <Icon name={photosExpanded ? 'chevron-up' : 'chevron-down'} size={14} />
                      {photosExpanded
                        ? t('imageViewer.showLess')
                        : t('imageViewer.showMore', { n: hiddenPhotosCount })}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {activeOrderTab === 'interaction' && (
            <div className="tab-content">
              <div className="interaction-container">
                <div className="interaction-section">
                  <h2>{t('orderDetails.interaction.statusTitle')}</h2>
                  {canEditStatus && (
                    <div className="status-master-select" style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {t('orderDetails.interaction.masterLabel')}
                      </label>
                      <CustomSelect
                        value={selectedMasterId}
                        options={masterOptions}
                        onChange={setSelectedMasterId}
                        minWidth="280px"
                        hasActiveFilter={Boolean(selectedMasterId)}
                      />
                    </div>
                  )}
                  <div className="status-inline">
                    {orderStatusesInline.map((row) => (
                      <button
                        type="button"
                        key={row.id}
                        data-order-status={row.id}
                        className={`btn btn-status ${order.status === row.id ? 'active' : ''}`}
                        onClick={() => handleOrderStatusChange(row.id)}
                        disabled={!canEditStatus || order.status === row.id || updatingStatus}
                      >
                        <span className="order-status-icon" aria-hidden="true">
                          <Icon name={row.icon} size={18} />
                        </span>
                        {row.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="interaction-section">
                  <h2>{t('orderDetails.interaction.noteTitle')}</h2>
                  <div className="note-form">
                    <textarea
                      className="note-textarea"
                      value={interactionNote}
                      onChange={(e) => setInteractionNote(e.target.value)}
                      placeholder={t('orderDetails.interaction.notePlaceholder')}
                      rows={5}
                    />
                    <div className="note-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setInteractionNote('')}
                        disabled={!interactionNote}
                      >
                        {t('orderDetails.interaction.clear')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSaveOrderNote}
                        disabled={!interactionNote.trim()}
                      >
                        {t('orderDetails.interaction.save')}
                      </button>
                    </div>
                  </div>
                  {orderCommentsLoading ? (
                    <p>{t('common.loading')}</p>
                  ) : orderComments && orderComments.length > 0 ? (
                    <div className="user-comments-list">
                      {orderComments.map((comment) => {
                        const cid = comment.id || comment.commentId || comment.createdAt;
                        const isEditing = editingCommentId && (comment.id === editingCommentId || comment.commentId === editingCommentId);
                        const authorName =
                          comment.authorFullName ||
                          comment.authorName ||
                          comment.createdByName ||
                          comment.createdBy ||
                          '';
                        const initial = authorName ? authorName.charAt(0).toUpperCase() : '?';
                        return (
                          <div key={cid} className="user-comment-card">
                            <div className="user-comment-card-header">
                              <div className="user-comment-card-author">
                                <span className="user-comment-avatar" aria-hidden="true">
                                  {initial}
                                </span>
                                <div className="user-comment-card-meta">
                                  {authorName && (
                                    <span className="user-comment-card-name">{authorName}</span>
                                  )}
                                  {comment.authorRole && (
                                    <span className="user-comment-role">
                                      {t(`users.roles.${comment.authorRole}`, comment.authorRole)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {comment.createdAt && (
                                <time className="user-comment-card-date" dateTime={comment.createdAt}>
                                  {new Date(comment.createdAt).toLocaleString(locale)}
                                </time>
                              )}
                            </div>
                            {isEditing ? (
                              <div className="user-comment-edit">
                                <textarea
                                  className="note-textarea"
                                  rows={3}
                                  value={editingCommentText}
                                  onChange={(e) => setEditingCommentText(e.target.value)}
                                />
                                <div className="user-comment-actions">
                                  <button className="btn btn-secondary btn-xs" type="button" onClick={cancelEditComment}>
                                    {t('common.cancel')}
                                  </button>
                                  <button
                                    className="btn btn-primary btn-xs"
                                    type="button"
                                    onClick={handleUpdateOrderComment}
                                    disabled={!editingCommentText.trim()}
                                  >
                                    {t('common.save')}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="user-comment-card-body">
                                <div className="user-comment-card-text">
                                  {comment.text || comment.comment}
                                </div>
                                <div className="user-comment-card-actions">
                                  <button
                                    type="button"
                                    className="btn-icon hover-trigger"
                                    onClick={() => startEditComment(comment)}
                                    title={t('common.edit')}
                                  >
                                    <Icon name="pencil" size={20} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-icon danger hover-trigger"
                                    onClick={() => handleDeleteOrderComment(comment)}
                                    title={t('common.delete')}
                                  >
                                    <Icon name="trash-2" size={20} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {lightboxIndex != null && lightboxIndex >= 0 && (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
};

export default OrderDetails;
